/* ---------------------------------------------------------------------------
   Which structures need rating, and which have been rated.

   THE BCIR GATE IS THE POINT OF THIS SCREEN. `MENU!BCIR` is the first thing the
   2009 program asks: overall condition Good or Fair, no load rating needed;
   Poor or Bad, rating required. That is the question this list answers for the
   whole register at once — the thing the sixteen workbooks could never do,
   because each one is a single bridge.

   THREE STATES, NOT TWO. A structure needs a rating, does not need one, or
   nobody has graded it. The third is drawn as an outline chip rather than folded
   into "no": a structure nobody has inspected is not a structure that has been
   found sound, and the register already refuses that conflation everywhere else.
   --------------------------------------------------------------------------- */

import { h } from '../../core/dom.ts';
import { button, card, cardHead, emptyState, tag, type TagRank } from '../../components/widgets.ts';
import { chainage } from '../../tenant/format.ts';
import { vocab } from '../../tenant/agency.ts';
import { ratingRequired } from '../../cbplr/index.ts';
import type { Bridge } from '../../data/types.ts';
import type { RatingRecord } from '../api.ts';
import { actions } from '../actions.ts';
import { can, type State } from '../store.ts';
import { scaleMax } from '../../data/derive.ts';
import { active } from '../../tenant/registry.ts';
import { reratingOf, type RerateStatus } from '../../data/rerating.ts';
import { latestAcceptedInspection } from '../damage.ts';
import { currentRating } from './ratings.ts';

/** The BCIR grades, worst last, that a 0-to-top condition scale falls into. */
const GRADES = ['G', 'F', 'F', 'P', 'B'] as const;

/**
 * An inspection's overall condition as a BCIR grade: its rating read against
 * the top of its scale, 0 Good to the top Serious, in DoR's five bands (Good,
 * Fair, Below average, Poor, Serious → G, F, F, P, B). Null where the scale
 * cannot be read.
 */
export function gradeOfInspection(rating: number, scale: string): string | null {
  const top = scaleMax(scale);
  if (top === null || !Number.isFinite(rating)) return null;
  const band = Math.min(4, Math.max(0, Math.round((rating / top) * 4)));
  return GRADES[band]!;
}

/**
 * The structure's overall condition, as the gate reads it (LC-24).
 *
 * THE LATEST ACCEPTED INSPECTION'S, where there is one, so a DoR or FWO bridge
 * with an accepted inspection is graded rather than *not graded*. DPWH's own
 * G/F/P/B, kept in `tenantFields`, is the fallback for a structure no accepted
 * inspection has graded — the whole DPWH register today. Returns null where
 * neither has been recorded, which the caller must not turn into a grade.
 */
export function conditionOf(b: Bridge): string | null {
  const inspected = latestAcceptedInspection(b.inspections);
  if (inspected !== null) {
    const grade = gradeOfInspection(inspected.rating, inspected.scale);
    if (grade !== null) return grade;
  }
  const raw = b.tenantFields['bcir'] ?? b.tenantFields['condition'] ?? null;
  if (raw === null) return null;
  const text = String(raw).trim().toUpperCase();
  return text === '' ? null : text;
}

/** Whether a bridge is due to be rated again, on this department's interval (LC-24). */
export function reratingFor(s: Readonly<State>, b: Bridge, today = new Date().toISOString().slice(0, 10)): RerateStatus {
  return reratingOf(b, s.ratings ?? [], s.defects ?? [], active().rerating, today);
}

/** The re-rating chip: due, and why; or the date it falls due. Null on a structure never rated. */
export function reratingChip(status: RerateStatus): HTMLElement | null {
  if (status.rated === 0) return null;
  if (status.due) {
    return tag('Rating due', 4, status.triggers.map((t) => (t.at ? `${t.label} (${t.at})` : t.label)).join(' · '));
  }
  return tag(`Re-rate by ${status.dueOn}`, 'none', 'The interval this department re-rates on, from the latest acceptance.');
}

/** The most recent rating for a structure, or null. */
export function latestRating(
  ratings: readonly RatingRecord[] | null,
  bridgeId: string,
): RatingRecord | null {
  if (ratings === null) return null;
  let best: RatingRecord | null = null;
  for (const r of ratings) {
    if (r.bridgeId !== bridgeId) continue;
    if (best === null || r.ratedAt > best.ratedAt) best = r;
  }
  return best;
}

/** The structures a search box's text should show. */
export function visible(bridges: readonly Bridge[], search: string): readonly Bridge[] {
  const q = search.trim().toLowerCase();
  if (q === '') return bridges;
  return bridges.filter((b) =>
    [b.id, b.name, b.roadName, b.route, b.district, b.river]
      .filter((v): v is string => typeof v === 'string')
      .some((v) => v.toLowerCase().includes(q)),
  );
}

function gateChip(b: Bridge, profile: State['profile']): HTMLElement {
  const condition = conditionOf(b);
  const required = ratingRequired(profile, condition);
  if (required === null) {
    return tag('Not graded', 'none', 'Nobody has recorded an overall condition, so the gate cannot be applied.');
  }
  return required
    ? tag(`${condition} · rating required`, 4, 'Poor or Bad — MENU!BCIR calls for a load rating.')
    : tag(`${condition} · no rating needed`, 1, 'Good or Fair — MENU!BCIR calls for no rating.');
}

function ratingChip(r: RatingRecord | null): HTMLElement {
  if (r === null) return tag('Never rated', 'none');
  const rf = r.governingRf;
  const label =
    rf === null
      ? `${r.state} · no rating factor`
      : `${r.state} · RF ${rf.toFixed(2)}`;
  /* Ranked on the answer, not the workflow: a draft that says 0.67 is more
     urgent reading than an accepted one that says 1.4. */
  const rank: TagRank = rf === null ? 'none' : rf < 1 ? 4 : rf < 1.2 ? 3 : 1;
  return tag(label, rank, r.postingRequired ? 'This rating concluded that posting is required.' : undefined);
}

export function structuresScreen(s: Readonly<State>): HTMLElement {
  const words = vocab();

  if (s.bridges === null) {
    return h(
      'div',
      { class: 'rt-scr' },
      card({ pad: true }, emptyState(
        s.backend === 'live'
          ? 'Reading the register.'
          : 'The register cannot be read until the deployment answers.',
      )),
    );
  }

  const rows = visible(s.bridges, s.search);

  return h(
    'div',
    { class: 'rt-scr' },
    card(
      { pad: true },
      cardHead('Register', `${rows.length} OF ${s.bridges.length} SHOWN`),
      h('input', {
        class: 'input',
        type: 'search',
        placeholder: `Search asset id, name, ${words.road}, ${words.area}…`,
        value: s.search,
        oninput: (e: Event) => actions.setSearch((e.target as HTMLInputElement).value),
      }),
      rows.length === 0
        ? emptyState('No structure matches that.')
        : h(
            'div',
            { class: 'rt-tbl' },
            h(
              'div',
              { class: 'rt-tbl__head' },
              h('span', { text: 'Structure' }),
              h('span', { text: words.road }),
              h('span', { text: 'Span' }),
              h('span', { text: 'Condition' }),
              h('span', { text: 'Governing rating' }),
              h('span', { text: '' }),
            ),
            ...rows.map((b) => {
              /* The governing rating (LC-22), not the newest: the lowest accepted
                 factor across its spans and girders. */
              const latest = s.ratings === null ? null : currentRating(s.ratings, b.id);
              return h(
                'div',
                {
                  class: ['rt-tbl__row', s.selected === b.id && 'rt-tbl__row--on'],
                  /* Picks, and does not toggle off: the selection is the structure
                     the page's explorer and 3D model are showing, and there is
                     always one of those. */
                  onclick: () => actions.select(b.id),
                },
                h(
                  'span',
                  null,
                  h('strong', { text: b.name }),
                  h('span', { class: 'rt-tbl__sub', text: b.id }),
                ),
                h(
                  'span',
                  null,
                  h('span', { text: b.route ?? b.roadName }),
                  h('span', { class: 'rt-tbl__sub', text: chainage(b.km) ?? '—' }),
                ),
                /* The span length, not the total: it is the number a rating is
                   computed on, and an average span is not a span. Dashed where
                   the register does not carry it — which is the honest state and
                   also a warning that the form will start empty. */
                h('span', { text: b.spanLength === null ? '—' : `${b.spanLength} m` }),
                h('span', null, gateChip(b, s.profile)),
                h('span', null, ratingChip(latest), reratingChip(reratingFor(s, b))),
                h(
                  'span',
                  null,
                  button('Rate', {
                    sm: true,
                    disabled: !can(s, 'rating.compute'),
                    ...(can(s, 'rating.compute')
                      ? {}
                      : { title: 'Your role may not compute a rating.' }),
                    onClick: () => actions.startRating(b.id),
                  }),
                ),
              );
            }),
          ),
    ),
  );
}
